/**
 * Leitura do contrato 429 do backend e cooldown por recurso.
 *
 * O backend responde 429 com `Retry-After` em segundos e `X-RateLimit-*`
 * (`mestti_backend/docs/rate-limiting.md`). Este módulo faz duas coisas, e só
 * elas:
 *
 * 1. **interpreta** a resposta — transforma o erro do Axios em algo tipado, com
 *    `retryAfterSeconds`;
 * 2. **guarda** o cooldown por chave de recurso, para quem faz polling
 *    consultar antes de disparar de novo.
 *
 * O que ele deliberadamente NÃO faz é dormir e repetir sozinho. Um interceptor
 * que faz `sleep` + retry esconde a pressão de quem a está causando, mantém a
 * requisição pendurada e, em mutação, pode repetir um efeito que já aconteceu no
 * servidor. Quem sabe se vale a pena tentar de novo é quem agenda o polling —
 * não o cliente HTTP.
 *
 * Também não existe cooldown global: um 429 no tempo-real da máquina A não pode
 * impedir salvar uma configuração, abrir outro módulo ou consultar a máquina B.
 * A chave é escolhida por quem chama.
 */

import type { AxiosError, AxiosInstance } from 'axios';

export const RATE_LIMIT_STATUS = 429;

/** Sem header legível, esperar um tempo plausível é melhor que martelar. */
export const DEFAULT_RETRY_AFTER_SECONDS = 30;

/** Teto de sanidade: um `Retry-After` absurdo não pode congelar a tela por horas. */
export const MAX_RETRY_AFTER_SECONDS = 3600;

export interface RateLimitInfo {
  /** Segundos até poder tentar de novo, já normalizado e limitado. */
  retryAfterSeconds: number;
  /** Instante (ms epoch) em que o cooldown termina. */
  retryAt: number;
  /** `X-RateLimit-Reset` do backend (ms epoch), quando presente. */
  rateLimitReset?: number;
}

export type RateLimitError = AxiosError & { rateLimit: RateLimitInfo };

const clampSeconds = (value: number): number =>
  Math.min(MAX_RETRY_AFTER_SECONDS, Math.max(1, Math.ceil(value)));

/**
 * `Retry-After` em segundos é o contrato do backend. HTTP-date é aceito como
 * defesa contra um proxy que reescreva o header — não porque alguém o emita.
 */
export const parseRetryAfter = (raw: unknown, now: number = Date.now()): number | null => {
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return clampSeconds(raw);
  }

  if (typeof raw !== 'string' || raw.trim() === '') {
    return null;
  }

  const asNumber = Number(raw.trim());
  if (Number.isFinite(asNumber)) {
    return clampSeconds(asNumber);
  }

  const asDate = Date.parse(raw);
  if (Number.isFinite(asDate)) {
    return clampSeconds((asDate - now) / 1000);
  }

  return null;
};

const readHeader = (error: AxiosError, name: string): unknown => {
  const headers = error.response?.headers as Record<string, unknown> | undefined;
  if (!headers) return undefined;
  // Axios normaliza para minúsculas, mas nem todo adapter/mock faz isso.
  return headers[name] ?? headers[name.toLowerCase()] ?? headers[name.toUpperCase()];
};

export const readRateLimitInfo = (
  error: AxiosError,
  now: number = Date.now(),
): RateLimitInfo | null => {
  if (error.response?.status !== RATE_LIMIT_STATUS) {
    return null;
  }

  const body = error.response?.data as { retry_after?: unknown } | undefined;
  const retryAfterSeconds =
    parseRetryAfter(readHeader(error, 'retry-after'), now) ??
    parseRetryAfter(body?.retry_after, now) ??
    DEFAULT_RETRY_AFTER_SECONDS;

  const resetHeader = Number(readHeader(error, 'x-ratelimit-reset'));

  return {
    retryAfterSeconds,
    retryAt: now + retryAfterSeconds * 1000,
    // O backend manda timestamp unix em SEGUNDOS; aqui tudo é ms.
    rateLimitReset: Number.isFinite(resetHeader) && resetHeader > 0 ? resetHeader * 1000 : undefined,
  };
};

export const isRateLimitError = (error: unknown): error is RateLimitError =>
  typeof error === 'object' &&
  error !== null &&
  (error as AxiosError).response?.status === RATE_LIMIT_STATUS;

export const getRateLimitInfo = (
  error: unknown,
  now: number = Date.now(),
): RateLimitInfo | null => {
  if (!isRateLimitError(error)) return null;
  // O interceptor já calculou `retryAt` no instante da resposta — que é o
  // instante certo. Só recalcula quando o erro não passou por ele.
  return (error as RateLimitError).rateLimit ?? readRateLimitInfo(error, now);
};

/**
 * Decora o erro com `rateLimit` e o rejeita. Não repete, não dorme, não desloga.
 *
 * 429 não é 401: a sessão continua válida. Tratar como sessão expirada mandaria
 * o operador para a tela de login no meio do turno por causa de um teto de
 * requisições.
 */
export const attachRateLimitInterceptor = (instance: AxiosInstance): void => {
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      const info = isRateLimitError(error) ? readRateLimitInfo(error as AxiosError) : null;
      if (info) {
        (error as RateLimitError).rateLimit = info;
      }
      return Promise.reject(error);
    },
  );
};

// ---------------------------------------------------------------------------
// Cooldown por recurso
// ---------------------------------------------------------------------------

const cooldowns = new Map<string, number>();

/** Registra o cooldown de um recurso. Chamado por quem agenda o polling. */
export const noteRateLimit = (key: string, info: RateLimitInfo): void => {
  const current = cooldowns.get(key) ?? 0;
  // Nunca encurtar um cooldown já registrado: duas respostas 429 do mesmo
  // recurso podem chegar fora de ordem.
  cooldowns.set(key, Math.max(current, info.retryAt));
};

export const cooldownRemainingMs = (key: string, now: number = Date.now()): number => {
  const until = cooldowns.get(key);
  if (until === undefined) return 0;
  const remaining = until - now;
  if (remaining <= 0) {
    cooldowns.delete(key);
    return 0;
  }
  return remaining;
};

export const isCoolingDown = (key: string, now: number = Date.now()): boolean =>
  cooldownRemainingMs(key, now) > 0;

export const clearRateLimitCooldown = (key?: string): void => {
  if (key === undefined) {
    cooldowns.clear();
    return;
  }
  cooldowns.delete(key);
};

/**
 * Açúcar para o caso comum: rodar um fetch periódico respeitando o cooldown.
 *
 * Devolve `{ skipped: true }` quando o recurso ainda está em cooldown, para o
 * chamador poder decidir se mostra algo. O dado antigo continua na tela — é
 * melhor que um erro ou um vazio.
 */
export const runWithRateLimitCooldown = async <T>(
  key: string,
  run: () => Promise<T>,
  now: () => number = Date.now,
): Promise<{ skipped: true } | { skipped: false; result: T }> => {
  if (isCoolingDown(key, now())) {
    return { skipped: true };
  }

  try {
    return { skipped: false, result: await run() };
  } catch (error) {
    const info = getRateLimitInfo(error, now());
    if (info) {
      noteRateLimit(key, info);
    }
    throw error;
  }
};
